import React from "react";

const LAST_UPDATED = "April 15, 2026";
const CONTACT_EMAIL = "tamir@people-leads.com";
const SITE_URL = "https://momentum.people-leads.com";

function Section({ title, children }) {
  return (
    <section className="mb-8">
      <h2 className="text-xl font-bold text-white mb-3">{title}</h2>
      <div className="text-[rgba(255,255,255,0.7)] leading-relaxed space-y-3">
        {children}
      </div>
    </section>
  );
}

export default function TermsOfService() {
  return (
    <div
      className="min-h-screen py-12 px-4"
      style={{ background: "#0a0a0a", fontFamily: "'Heebo', sans-serif" }}
      dir="ltr"
    >
      <div className="max-w-3xl mx-auto">
        <h1 className="text-3xl font-black text-white mb-2">Terms of Service</h1>
        <p className="text-sm text-[rgba(255,255,255,0.4)] mb-10">
          Last updated: {LAST_UPDATED}
        </p>

        <Section title="1. Acceptance of Terms">
          <p>
            By accessing or using Mom3ntum at{" "}
            <a href={SITE_URL} className="underline" style={{ color: "#c8f135" }}>
              {SITE_URL}
            </a>{" "}
            (the "Service"), you agree to be bound by these Terms of Service
            ("Terms"). If you do not agree to these Terms, do not use the
            Service.
          </p>
        </Section>

        <Section title="2. Description of Service">
          <p>
            Mom3ntum is an engagement platform where users earn points by
            completing social engagement tasks for a chance to win event tickets
            at face value. The Service includes campaign participation,
            leaderboard rankings, referral programs, and surveys.
          </p>
          <p>
            Mom3ntum does not sell tickets directly. Ticket availability,
            pricing, and fulfillment depend on campaign-specific terms and
            third-party providers.
          </p>
        </Section>

        <Section title="3. Eligibility">
          <p>
            You must be at least 16 years old to use the Service. By creating an
            account, you represent that you meet this age requirement and that
            the information you provide is accurate.
          </p>
        </Section>

        <Section title="4. Account Registration">
          <p>
            You may register using a third-party login provider (Google,
            Facebook, LinkedIn, Twitter/X, or Discord). You are responsible for
            maintaining the security of your account and for all activity under
            your account.
          </p>
          <p>
            You agree to provide accurate, current, and complete information.
            Creating multiple accounts to gain unfair advantage is prohibited and
            may result in disqualification and account termination.
          </p>
        </Section>

        <Section title="5. Points and Engagement Tasks">
          <p>
            Points are earned by completing engagement tasks as defined in each
            campaign. Tasks may include social media interactions, referrals, and
            other activities.
          </p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li>Points have no cash value and cannot be transferred, sold, or exchanged outside the Service.</li>
            <li>Some tasks require manual review and approval before points are awarded.</li>
            <li>We reserve the right to revoke points earned through fraudulent or abusive behavior.</li>
            <li>Point balances and leaderboard rankings are specific to each campaign.</li>
          </ul>
        </Section>

        <Section title="6. Campaigns and Prizes">
          <p>
            Campaigns are time-limited promotions with specific rules, point
            structures, and prizes. Campaign-specific terms will be communicated
            on the relevant campaign page and take precedence over these general
            Terms where applicable.
          </p>
          <p>
            We reserve the right to modify, suspend, or cancel any campaign at
            any time. Prize fulfillment is subject to availability and may
            involve third-party providers.
          </p>
        </Section>

        <Section title="7. User Conduct">
          <p>You agree not to:</p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li>Use bots, scripts, or automated tools to complete tasks</li>
            <li>Submit fake, misleading, or fraudulent task completions</li>
            <li>Create multiple accounts or impersonate others</li>
            <li>Harass, abuse, or harm other users</li>
            <li>Attempt to manipulate leaderboard rankings</li>
            <li>Reverse-engineer, hack, or disrupt the Service</li>
            <li>Use the Service for any illegal purpose</li>
          </ul>
          <p>
            Violation of these rules may result in point revocation, account
            suspension, or permanent ban at our sole discretion.
          </p>
        </Section>

        <Section title="8. Intellectual Property">
          <p>
            All content, trademarks, logos, and design elements of the Service
            are the property of Mom3ntum or its licensors. You may not copy,
            modify, distribute, or create derivative works without prior written
            consent.
          </p>
          <p>
            By submitting content (such as task screenshots or survey responses),
            you grant us a non-exclusive, royalty-free license to use that
            content for operating and improving the Service.
          </p>
        </Section>

        <Section title="9. Third-Party Services">
          <p>
            The Service integrates with third-party platforms including Auth0,
            Google Analytics, Meta, and social media networks. Your use of these
            services is governed by their respective terms and privacy policies.
            We are not responsible for the practices of third-party services.
          </p>
        </Section>

        <Section title="10. Disclaimers">
          <p>
            The Service is provided "as is" and "as available" without
            warranties of any kind, either express or implied, including but not
            limited to implied warranties of merchantability, fitness for a
            particular purpose, and non-infringement.
          </p>
          <p>
            We do not guarantee that the Service will be uninterrupted,
            error-free, or secure. We are not responsible for any loss of points,
            data, or prizes resulting from technical issues.
          </p>
        </Section>

        <Section title="11. Limitation of Liability">
          <p>
            To the maximum extent permitted by law, Mom3ntum and its officers,
            directors, employees, and agents shall not be liable for any
            indirect, incidental, special, consequential, or punitive damages
            arising from your use of the Service, including but not limited to
            loss of profits, data, or goodwill.
          </p>
        </Section>

        <Section title="12. Termination">
          <p>
            We may suspend or terminate your account at any time for violation
            of these Terms or for any reason at our discretion. You may delete
            your account at any time by visiting our{" "}
            <a href="/data-deletion" className="underline" style={{ color: "#c8f135" }}>
              Data Deletion page
            </a>{" "}
            or contacting us.
          </p>
          <p>
            Upon termination, your right to use the Service ceases immediately.
            Any accumulated points are forfeited.
          </p>
        </Section>

        <Section title="13. Changes to Terms">
          <p>
            We may update these Terms from time to time. Changes will be posted
            on this page with an updated "Last updated" date. Continued use of
            the Service after changes constitutes acceptance of the revised
            Terms.
          </p>
        </Section>

        <Section title="14. Governing Law">
          <p>
            These Terms shall be governed by and construed in accordance with
            the laws of the State of Israel, without regard to its conflict of
            law provisions.
          </p>
        </Section>

        <Section title="15. Contact Us">
          <p>
            For questions about these Terms, contact us at:
          </p>
          <p>
            <strong>Email:</strong>{" "}
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              className="underline"
              style={{ color: "#c8f135" }}
            >
              {CONTACT_EMAIL}
            </a>
          </p>
        </Section>

        <div
          className="mt-12 pt-6 text-center text-xs"
          style={{ borderTop: "1px solid rgba(255,255,255,0.1)", color: "rgba(255,255,255,0.25)" }}
        >
          Mom3ntum &copy; {new Date().getFullYear()}
        </div>
      </div>
    </div>
  );
}
